/** 
 * Plugin Name: E.T.T – Easy Time Tracker for WooCommerce
 * Description: Javascript functions for the plugin.
 * Author: dansart
 * Author URI: https://dans-art.ch
 * License: GPLv2 or later
 */

import Choices from "choices.js";
import "choices.js/public/assets/styles/choices.min.css"; 

const wc_timetracker = (() => {

    //Starts Push Service
    var swRegistration = null;
    const time_to_inactivity = 120; //The time in seconds in order to mark a tracker as inactive. Default: 120

    const { __ } = wp.i18n;

    let timetracker_data = null;

    let customer_select_choices = null;
    let product_select_choices = null;


    /**
     * Formats a dropdown client list item based on the data attributes set on the element.
     * Line 1 contains the title and total amount, line 2 name and company name, Line 3 has custom meta.
     * Only data fields with the "title_" prefix will be used
     * @param {object} data The data object containing the element and text.
     * @returns {object} A span element containing the formatted text.
     */
    function format_dropdown_client(data, classNames) {
        if (data.element === undefined || data.element.getAttribute('data-title_date') === null) {
            return `
        <div
          class="choices-item-no-title ${classNames.item} ${classNames.itemChoice}"
          data-choice
          data-id="${data.id}"
          data-value="${data.value}"
          ${data.disabled
                    ? 'data-choice-disabled aria-disabled="true"'
                    : 'data-choice-selectable'}
        >
        ${data.label}
        </div>
      `;
        }

        let title_items = {};
        for (const item in data.element.dataset) {
            if (item.startsWith('title_')) {
                title_items[item] = data.element.dataset[item];
            }
        }

        let line1 = (title_items.title_date !== undefined) ? title_items.title_date : '';
        line1 += (title_items.title_total !== undefined && title_items.title_total.length > 0) ? ' | ' + title_items.title_total : '';
        delete title_items.title_date;
        delete title_items.title_total;

        let line2 = (title_items.title_name !== undefined) ? title_items.title_name : '';
        line2 += (title_items.title_company !== undefined && title_items.title_company.length > 0) ? ', ' + title_items.title_company : '';
        delete title_items.title_name;
        delete title_items.title_company;

        let line3 = Object.values(title_items).filter(value => value !== '').join(' | ');

        const span = document.createElement('span');
        span.innerHTML = `${line1}<br/><b>${line2}</b><br/>${line3}`;
        return `
        <div
          class="choices-item-title ${classNames.item} ${classNames.itemChoice}"
          data-choice
          data-id="${data.id}"
          data-value="${data.value}"
          ${data.disabled
                ? 'data-choice-disabled aria-disabled="true"'
                : 'data-choice-selectable'}
        >
        ${line1}<br/><b>${line2}</b><br/>${line3}
        </div>
      `;
    }



    /**
     * Retrieves the products for a given order ID from the server.
     * Sends a POST request to the server with the action 'get_products'.
     * If the response is successful, it will update the product dropdown with the received data.
     * If there was an error, it will log the error and hide the product dropdown.
     * @param {number} order_id The ID of the order to get the products for.
     * @returns {promise} A promise with the response from the server.
     */
    async function get_products(order_id) {
        const data = new URLSearchParams();
        data.append('action', 'ett_easy_time_tracker_ajax');
        data.append('do', 'get_products');
        data.append('tt_customer_select', order_id);
        data.append('tt_nonce', get_timetracker_inputs('tt_nonce'));

        try {
            const response = await fetch(easyTimeTrackerGlobalVars.da_ajaxurl, {
                method: 'POST',
                body: data
            });

            if (!response.ok) {
                throw new Error('Network response was not ok: ' + response.statusText);
            }

            const items = await response.json();

            if (items.error && items.error.length > 0) {
                tt_log(items.error);
                hide_product_dropdown();
                return false;
            } else {
                timetracker_data = items.success;
                document.querySelector('#tt_customer_products_select').innerHTML = items.success.options;
                init_products_dropdown();
                show_product_dropdown();
                return true;
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            hide_product_dropdown();
            return false;
        }
    }


    /**
     * Saves the amount to the database via ajax
     * It takes the existing one and adds the new amount to it
     * Sends the description of the job with the current date. It will add the description, if
     * there was a time already tracked for this day. 
     */
    async function save_product(hours = null) {

        const data = get_timetracker_inputs();
        data.action = 'ett_easy_time_tracker_ajax';
        data.do = 'save_qty';
        if (hours > 0) {
            data.hours = hours;
        }
        try {
            const response = await fetch(easyTimeTrackerGlobalVars.da_ajaxurl, {
                method: 'POST',
                body: new URLSearchParams(data)
            });
            const items = await response.json();
            if (items.error.length > 0) {
                tt_log(items.error, 'error');
            }
            else {
                tt_log(items.success, 'success');

                //reload the products
                const order_id = document.querySelector('#tt_customer_select').selectedOptions[0].value;
                get_products(order_id);
                hide_time_tracker();
            }
        } catch (error) {
            console.error('An error occurred: ' + error.message, 'error');

        }
    }



    /**
     * Returns an object containing the inputs of the Timetracker form.
     * It includes the official fields (tt_customer_select, tt_customer_products_select, tt_meta_description, tt_nonce),
     * as well as any additional metadata fields (with field_id as key and input value as value).
     * If a field_id is specified, it will return the input element with that ID.
     * @param {string} [field_id] The ID of the field to return the input element for.
     * @returns {object} The object containing the inputs of the Timetracker form.
     */
    function get_timetracker_inputs(field_id = "") {
        if (field_id.length > 0) {
            return document.querySelector('form#timetracker #' + field_id).value;
        }
        let data = {};
        //Get the inputs
        const exclude_fields = [];
        const official_fields = ['tt_customer_select', 'tt_customer_products_select', 'tt_meta_description', 'tt_nonce'];
        for (const input of document.querySelectorAll('form#timetracker select, form#timetracker input, form#timetracker textarea')) {
            //filter out unwanted metadata
            if (exclude_fields.includes(input.id)) {
                continue;
            }
            if (input.id === undefined || input.id === '') {
                continue;
            }
            if (official_fields.includes(input.id)) {
                data[input.id] = input.value;
            } else {
                //Additional metadata
                data['meta__' + input.id] = input.value;
            }


        }
        //Add additional data
        data.hours = get_current_time_in_hours();
        data.start_time = document.querySelector('#tt_start_time').getAttribute('stime');
        data.current_time = new Date().getTime();
        data.meta_date = document.querySelector('#tt_start_time').getAttribute('stime');

        return data;
    }
    /**
     * Shows the Time Tracker
     * @param {float} qty The quantity for the selected product
     */
    function show_time_tracker(qty) {
        document.querySelector('#tt_timetracker').style.display = 'block';
        document.querySelector('#tt_start_value .value').innerHTML = qty;

        /**
       * Additional meta data. Use the filter "tt_get_products_return" to add custom inputs and outputs.
       * Inputs must be a input field with the same ID as the name (input)
       * Outputs must have a child element with the class .value. They are only for displaying data.
       */
        for (let out in timetracker_data.custom_output) {
            const outputElement = document.querySelector(`#${out} .value`);
            if (outputElement) {
                outputElement.innerHTML = timetracker_data.custom_output[out];
            }
        }

        for (let input in timetracker_data.custom_input) {
            const inputElement = document.querySelector(`#${input}`);
            if (inputElement) {
                inputElement.value = timetracker_data.custom_input[input];
            }
        }
    }


    /**
     * Hides the Time Tracker
     * 
     */
    function hide_time_tracker() {
        document.querySelector('#tt_timetracker').style.display = 'none';
    }

    /**
     * Shows the "Products" dropdown
     */
    function show_product_dropdown() {
        document.querySelector('#tt_customer_products').style.display = 'block';
    }

    /**
     * Hides the "Products" dropdown
     */
    function hide_product_dropdown() {
        document.querySelector('#tt_customer_products').style.display = 'none';
    }

    /**
     * Runs on click event. Changes the Name of the button and starts or stops the tracker
     * @param {object} btn The Button object 
     */
    async function time_tracker_click(btn) {
        tt_clear_log();
        const ctext = btn.textContent;

        if (ctext === __('Start', 'ett-easy-time-tracker-for-woocommerce')) {
            change_button_text(__('Stop', 'ett-easy-time-tracker-for-woocommerce'));
            run_time_tracker();
        } else {
            change_button_text(__('Start', 'ett-easy-time-tracker-for-woocommerce'));
            stop_time_tracker();
        }
    }

    /**
     * Gets all active trackers and displays them in the UI.
     * It sends a POST request to the server with the action 'tt_get_trackers'.
     * If the response is successful, it will display the active trackers in the UI.
     * If there are no active trackers, it will remove all existing active trackers from the UI.
     * @returns {promise} A promise with the active trackers as an object or false if there was an error.
     */
    async function get_trackers() {
        try {
            const data = new URLSearchParams();
            data.append('action', 'ett_easy_time_tracker_ajax');
            data.append('do', 'tt_get_trackers');
            data.append('tt_nonce', get_timetracker_inputs('tt_nonce'));

            const response = await fetch(easyTimeTrackerGlobalVars.da_ajaxurl, {
                method: 'POST',
                body: data
            });

            if (!response.ok) {
                throw new Error('Network response was not ok: ' + response.statusText);
            }

            const items = await response.json();

            if (items.error.length > 0) {
                console.error(items.error);
                return false;
            }

            if (items.success.length === 0) {
                // Remove all active trackers if existing
                document.querySelector('#tt_active_trackers').innerHTML = '';
                return {};
            }

            display_active_trackers(items.success);
            return items.success;

        } catch (error) {
            console.error('Error fetching trackers:', error);
            return false;
        }
    }

    /**
     * Returns the status of a tracker.
     * @param {number} tracker_id The ID of the order to get the status for.
     * @returns {boolean|string} The status of the tracker, or false if the tracker does not exist.
     */
    async function get_tracker_status(tracker_id) {
        const trackers = await get_trackers();
        return new Promise((resolve) => {
            if(tracker_id === undefined){
                resolve(false);
            }
            if (trackers['order_' + tracker_id] === undefined) {
                resolve(false);
            }
            resolve(trackers['order_' + tracker_id]['status'] === undefined ? false : trackers['order_' + tracker_id]['status']);
        });
    }


    /**
     * Displays all active trackers in the UI.
     * @param {object} trackers An object with active trackers.
     */
    function display_active_trackers(trackers) {
        if (typeof trackers !== 'object') {
            return;
        }
        let items = '';
        const current_time = new Date().getTime();
        document.querySelectorAll('#tt_active_trackers > *').forEach(element => element.remove());
        Object.entries(trackers).forEach(([key, item]) => {

            //Skip if the tracker is stopped
            if (item.status === 'stopped' || item.status === 'cleared') {
                console.log(`Ignored tracker ${item.tt_customer_select}, status: ${item.status}`);
                return;
            }
            //Skip active item
            const current = get_timetracker_inputs();

            if (current.tt_customer_select === item.tt_customer_select && current.tt_customer_products_select === item.tt_customer_products_select) {
                return;
            }

            //Add the new line
            const name = get_client_by_order_id(item.tt_customer_select);
            const last_active = Math.floor((current_time - item.current_time) / 1000); //Seconds
            const last_active_min = (last_active / 60).toFixed(0);
            const hours = parseFloat(ms_to_hours(current_time - item.start_time));

            const resume_text = __('Resume', 'ett-easy-time-tracker-for-woocommerce');
            const cancel_text = __('Cancel', 'ett-easy-time-tracker-for-woocommerce');
            const la = __('Last active', 'ett-easy-time-tracker-for-woocommerce');
            const tracked_in_h = __('Tracked time in hours', 'ett-easy-time-tracker-for-woocommerce');

            items += `<div id="tracker_${item.tt_customer_select}" class="tracker">
        <span class="name">${name}<br/>
            <span class='description'>${item.tt_meta_description}</span>
        </span>
        <span class="hours" title="${tracked_in_h}">${hours}h</span>
        <span class="last_active" title="${la}">${last_active_min}min</span>`; //Displays minutes
            if (last_active >= time_to_inactivity) {
                //If it is longer than 2 minutes since the last update
                items += `<button class="resume wp-element-button"  data-tracker='${JSON.stringify(item)}'>${resume_text}</button>`;
            } else {
                items += `<span></span>`; //Just a placeholder to keep a consistent design

            }
            items += `<button class="cancel wp-element-button"  title="${cancel_text}" data-tracker='${JSON.stringify(item)}'><span class="dashicons dashicons-no-alt"></span></button>`;
            items += `</div>`;
        });
        if (items.length > 0) {
            const heading = __('Active trackers', 'ett-easy-time-tracker-for-woocommerce');
            document.getElementById('tt_active_trackers').insertAdjacentHTML('beforeend', `<h3>${heading}</h3>`);
        }
        document.getElementById('tt_active_trackers').insertAdjacentHTML('beforeend', items);
    }

    /**
     * Returns the name of the client based on the order_id.
     * It loops through all the client options in the select and returns the data-title_name of the option with the value matching the order_id.
     * If no matching client is found, it will return null.
     * @param {number} order_id The ID of the order to get the client name for.
     * @returns {string|null} The name of the client, or null if no matching client is found.
     */
    function get_client_by_order_id(order_id) {
        try {
            const clients = document.getElementById('tt_customer_select')?.options || [];
            order_id = parseInt(order_id);
            for (const option of Array.from(clients)) {
                if (parseInt(option.value) === order_id) {
                    return option.getAttribute('data-title_name');
                }
            }
            return null;
        } catch (error) {
            console.error(`Error getting client by order id: ${error}`);
            return null;
        }
    }

    /**
     * Cancels the tracking for the given order_id.
     * It will ask for confirmation before clearing the tracker.
    
     * @param {object} data The data object containing the tt_customer_select, tt_customer_products_select, start_time and meta_description
     * @returns {boolean} true if the tracking is cancelled, false otherwise
     */
    async function cancel_tracking(data) {
        const name = get_client_by_order_id(data.tt_customer_select);
        const check = confirm(__('Are you sure that you want to discard the tracking for %s?', 'ett-easy-time-tracker-for-woocommerce').replace('%s', name));
        if (!check) {
            return;
        }
        clear_tracking(data.tt_customer_select);

    }
    /**
     * Resumes a tracker
     * @param {object} data The data object containing the tt_customer_select, tt_customer_products_select, start_time and meta_description
     */
    async function resume_tracking(data) {

        //Check if the order is still inactive
        const current_trackers = await get_trackers();
        const current_time = new Date().getTime();
        const item = current_trackers['order_' + data.tt_customer_select];

        //Check is resuming is possible
        if (item !== 'undefined') {
            const last_active = Math.floor((current_time - item.current_time) / 1000); //Time in seconds
            if (last_active < time_to_inactivity) {
                tt_log(__('Failed to start the tracking. It is used by another person / machine', 'ett-easy-time-tracker-for-woocommerce'));
                return;
            }
        }

        //Set the customer
        //Check if the item exists 
        const selected_client = document.querySelector('#tt_customer_select option[value="' + data.tt_customer_select + '"]');
        if (selected_client.value.length === 0 || selected_client.value === 'null') {
            tt_log(__('Failed to resume tracking. Client not found', 'ett-easy-time-tracker-for-woocommerce'));
            return;
        }
        customer_select_choices.setChoiceByValue(data.tt_customer_select);

        set_start_time_html(data.start_time);
        change_button_text("Stop");

        //Load the products
        await get_products(data.tt_customer_select);

        const selected_option = document.querySelector('#tt_customer_products_select option[value="' + data.tt_customer_products_select + '"]');
        if (selected_option.value.length === 0 || selected_option.value === 'null') {
            tt_log(__('Failed to resume tracking. Product ID not found', 'ett-easy-time-tracker-for-woocommerce'));
            return;
        }
        product_select_choices.setChoiceByValue(data.tt_customer_products_select);

        show_time_tracker(data.hours);
        run_time_tracker();

        //Set description
        document.querySelector('#tt_meta_description').value = data.tt_meta_description;

        //Set other meta
        try {
            Object.entries(data).forEach(([key, value]) => {
                if (key.startsWith('meta__')) {
                    const input_key = key.replace(/^meta__/, '');
                    document.querySelector('#' + input_key).value = value;
                }
            });

        } catch (error) {
            console.error(error);
        }

        //Update the tracker list
        document.querySelector('#wc_time_tracker #tracker_' + data.tt_customer_select).remove();
    }


    /**
     * Clears the trackers for a given order ID.
     * If no order ID is provided, it will clear all trackers. Errors or success will be logged via tt_log
     * @param {number} [order_id] The ID of the order to clear the trackers for.
     * @returns {bool} True on success, false on error
     */
    async function clear_tracking(order_id = null) {
        const data = {
            action: 'ett_easy_time_tracker_ajax',
            do: 'tt_clear_trackers',
            tt_nonce: get_timetracker_inputs('tt_nonce')
        };

        if (order_id !== null) {
            data.tt_customer_select = order_id;
        }

        try {
            const params = new URLSearchParams(data);

            const response = await fetch(easyTimeTrackerGlobalVars.da_ajaxurl, {
                method: 'POST',
                body: params
            });

            if (!response.ok) {
                throw new Error('Network response was not ok: ' + response.statusText);
            }

            const items = await response.json();

            if (items.error.length > 0) {
                tt_log(items.error, 'error');
                return false;
            }
            if (order_id !== null) {
                //Remove the item, if found
                document.querySelector('#tt_active_trackers #tracker_' + order_id).remove();
            }
            tt_log(items.success, 'success');
            return true;

        } catch (error) {
            console.error('Error fetching trackers:', error);
            return false;
        }
    }


    /**
     * Adds the time in minutes to the tracker and stops the tracker.
     * Prompts the user for the time in minutes to add. If the user cancels, the function will return false.
     * @param {object} btn The Button object
     * @returns {bool} True on success, false on cancel
     */
    function time_tracker_fix_click() {
        const prompt = window.prompt(__('Set the time in minutes', 'ett-easy-time-tracker-for-woocommerce'));
        if (prompt === null) {
            return false;
        }
        const minutes = parseInt(prompt);
        change_button_text(__('Start', 'ett-easy-time-tracker-for-woocommerce'));
        stop_time_tracker(minutes);
    }

    /** 
     * Set the Text of the main button
     */
    function change_button_text(txt) {
        document.querySelector("#tt_action_button").textContent = txt;
    }

    /**
     * Starts the tracking.
     * Shows the Notification that the tracking has started
     * Shows every 30 Minutes a Notification as a reminder
     */
    async function run_time_tracker() {
        document.querySelector('#tt_action_fix_time_button').style.display = 'block';
        customer_select_choices.disable();
        product_select_choices.disable();

        const order_id = document.querySelector('#tt_customer_select').value;

        //Check if the tracker can be started. If it is running, it cannot be overridden.
        const status = await get_tracker_status(order_id);
        if (status === 'running') {
            tt_log(__('Failed to start the tracker. This tracker is still running. You can start a new tracker in about %d seconds', 'ett-easy-time-tracker-for-woocommerce').replace('%d', time_to_inactivity), 'error');
            //Reset the buttons
            document.querySelector('#tt_action_fix_time_button').style.display = 'none';
            customer_select_choices.disable();
            product_select_choices.disable();
            change_button_text('Start');
            return;
        }

        if (document.querySelector('#tt_start_time').getAttribute('stime') === null) {
            set_start_time_html();
        }

        document.querySelector('#tt_stop_time .value').innerHTML = '';

        //Show the notification
        showLocalNotification('Time Tracker', 'Tracking started for ' + get_current_customer_name(), 'time_tracker_notify');

        var ct = null;
        document.tt_timer = window.setInterval(function () {
            ct = time_tracker_current();
        }, 1000);

        document.tt_timer_notification = window.setInterval(function () {
            showLocalNotification('Time Tracker', 'Tracking still running for ' + get_current_customer_name() + '\r\nCurrent time: ' + ct, 'time_tracker_notify');
        }, 1000 * 60 * 30); //Send a notification every 30 Min

        set_heartbeat_interval();
        send_heartbeat();
    }

    /**
     * Sets an interval to send a heartbeat to the server every minute to update the transient.
     * If there is already a heartbeat interval running, it will not start a new one.
     * @returns {bool} True on success, false on error
     */
    function set_heartbeat_interval() {
        if (document.tt_heartbeat !== undefined && document.tt_heartbeat.length > 0) {
            //Skip it, if there is already a heartbeat interval running
            return false;
        }
        //Send a heartbeat every minute to update the transient
        document.tt_heartbeat = window.setInterval(function () {
            send_heartbeat();
        }, 60000);
    }

    /**
     * Sends a heartbeat to the server
     * This function is called from the set_heartbeat_interval function.
     * It will not send the heartbeat when no customer or product is selected.
     */
    async function send_heartbeat(status = 'running') {
        const data = get_timetracker_inputs();
        //Do not send the heartbeat when no customer or product is selected
        if (data.tt_customer_select === 'null' || data.tt_customer_products_select === '' || data.tt_customer_products_select === 'null') {
            return;
        }

        //Only send the heartbeat when the timetracker is running
        if (document.querySelector('#tt_start_time .value').innerHTML === '') {
            return;
        }

        data.action = 'ett_easy_time_tracker_ajax';
        data.do = 'tt_update_heartbeat';
        data.status = status;

        try {
            const params = new URLSearchParams(data);

            const response = await fetch(easyTimeTrackerGlobalVars.da_ajaxurl, {
                method: 'POST',
                body: params
            });

            if (!response.ok) {
                throw new Error('Network response was not ok: ' + response.statusText);
            }

            const items = await response.json();

            if (items.error.length > 0) {
                console.error(items.error);
                return false;
            }

            return items.success;

        } catch (error) {
            console.error('Error fetching trackers:', error);
            return false;
        }
    }

    /**
     * Clears the heartbeat interval, stopping the sending of heartbeats to the server.
     */
    function stop_heartbeat_interval() {
        clearInterval(document.tt_heartbeat);
    }

    /**
     * Sets the displayed Start time as well as the Attribute for the starttime
     * @param {*} time_in_seconds Time in Seconds from Date().getTime() function
     */
    function set_start_time_html(time_in_seconds = null) {
        time_in_seconds = (time_in_seconds !== null) ? time_in_seconds : new Date().getTime();
        time_in_seconds = parseInt(time_in_seconds);

        var date = new Date(time_in_seconds).toLocaleDateString("de-DE");
        var time = new Date(time_in_seconds).toLocaleTimeString("de-DE");

        document.querySelector('#tt_start_time').setAttribute('stime', time_in_seconds);
        document.querySelector('#tt_start_time .value').innerHTML = date + ' - ' + time;
    }

    /**
     * Deletes the Starttime. Runs after Timetracker is finished.
     */
    function clear_start_time_html() {
        document.getElementById('tt_start_time').removeAttribute('stime');
        document.querySelector('#tt_start_time .value').innerHTML = '';
    }

    /**
     * Stops the tracking.
     * Clears the Timers
     * Calls function to save the amount
     * @param {int} minutes The amount of minutes to add. If nothing defined, it will calculate the time
     */
    async function stop_time_tracker(minutes = null) {
        //Close the notification
        closeLocalNotification('time_tracker_notify');

        stop_heartbeat_interval();

        document.querySelector('#tt_action_fix_time_button').style.display = 'none';
        customer_select_choices.enable();
        product_select_choices.enable();
        let date = new Date().toLocaleDateString("de-DE");
        let time = new Date().toLocaleTimeString("de-DE");
        document.querySelector('#tt_stop_time .value').innerHTML = (date + ' - ' + time);

        //stop the current counter
        clearInterval(document.tt_timer);
        clearInterval(document.tt_timer_notification);
        let qty_to_save = null;
        if (minutes !== null) {
            const hours = (parseInt(minutes) / 60).toFixed(2);
            qty_to_save = hours;
        }
        await save_product(qty_to_save);

        send_heartbeat('stopped'); //Mark the order as stopped

        //Update the current tracker list
        get_trackers();

        //Clear the description
        document.querySelector('#tt_timetracker #tt_meta_description').value = "";

        clear_start_time_html();
    }

    /**
     * Returns the current time in hours from the start of the tracker.
     * If the time is shorter than 5 minutes, it adds 10 minutes (0.1h) to the result.
     * @return {float} The current time in hours (with 2 decimal places)
     */
    function get_current_time_in_hours() {

        var otime = document.querySelector('#tt_start_time').getAttribute('stime');
        var ctime = new Date().getTime();

        var calcTime = (ctime - otime); //Time in Milliseconds 

        var hrs = parseFloat(ms_to_hours(calcTime));
        var oqty = parseFloat(document.querySelector('#tt_start_value .value').textContent);
        oqty = (oqty.length > 0) ? oqty : 0;
        if (hrs === 0) {
            //Add 10 Minutes (0.1h) if time is shorter than 5 Minutes
            hrs = 0.1;
        }
        return parseFloat(hrs + oqty).toFixed(2);
    }


    /**
     * Gets the time since the tracking has started.
     * Updates the "Current Time" field
     */
    function time_tracker_current() {
        var otime = document.querySelector('#tt_start_time').getAttribute('stime');
        var ctime = new Date().getTime();

        var calcTime = (ctime - otime);//Time in Milliseconds
        var hrs = parseFloat(ms_to_hours(calcTime));
        var ct = ms_to_time(calcTime) + " (" + hrs + "h)";
        document.querySelector('#tt_current_time .value').innerHTML = ct;
        return ct;
    }

    /**
     * Formats Milliseconds to hh:mm:ss
     * @param {int} s Milliseconds
     */
    function ms_to_time(s) {
        var ms = s % 1000;
        s = (s - ms) / 1000;
        var secs = s % 60;
        s = (s - secs) / 60;
        var mins = s % 60;
        var hrs = (s - mins) / 60;

        return pad(hrs) + ':' + pad(mins) + ':' + pad(secs);

    }

    /**
     * Formats Milliseconds to hours with one decimal (1.5)
     * @param {int} s Milliseconds
     */
    function ms_to_hours(s) {
        var ms = s % 1000;
        s = (s - ms) / 1000;
        var secs = s % 60;
        s = (s - secs) / 60;
        var mins = s % 60;
        var hrs = (s - mins) / 60;

        var calcT = hrs + (mins / 60);
        return (Math.round(calcT * 100) / 100).toFixed(1);

    }

    /**
     * Adds leading zeros
     * @param {int} n  Number to modify
     * @param {int} z Total length of the number
     */
    function pad(n, z = 2) {
        return ('00' + n).slice(-z);
    }

    /**
     * Sends a Message to the Log.
     * @param {string} msg The Message to show
     * @param {string} type Should be "success", "error" or "default"
     */
    function tt_log(msg, type = 'default') {
        document.getElementById('tt_log').insertAdjacentHTML('beforeend', `<div class="${type}">${msg}</div>`);
    }

    /**
     * Clears the Log
     * This function will clear the log and remove all previous messages.
     */
    function tt_clear_log() {
        document.getElementById('tt_log').innerHTML = "";
    }


    /**
     * Returns the current customer name in the format "Company - Name"
     * @returns {string} The current customer name
     */
    function get_current_customer_name() {
        var selector = document.querySelector('#tt_customer_select').selectedOptions[0];
        var company = selector.getAttribute('data-title_company');
        var name = selector.getAttribute('data-title_name');
        return company + " - " + name;
    }

    /**
     * Displays the notification
     * @param {string} title The Title of the notification
     * @param {string} body The Body Text of the notification
     * @param {string} thetag The Tag of the notification
     */
    const showLocalNotification = (title, body, thetag = "default") => {
        if (swRegistration === null) {
            return false;
        }
        dataobj = new Object;
        dataobj['url'] = window.location.href;
        const options = {
            body: body,
            tag: thetag,
            icon: easyTimeTrackerGlobalVars.wp_frontend_url + "include/images/icon.ico",
            badge: easyTimeTrackerGlobalVars.wp_frontend_url + "include/images/icon.ico",
            vibrate: [10, 50, 50, 50, 10],
            data: dataobj
        };
        var not = swRegistration.showNotification(title, options);


        return not;
    }

    /**
     * Removes the notification when time tracker stops
     * @param {string} thetag The Tag of the notification. Set on showLocalNotification
     */
    function closeLocalNotification(thetag) {
        if (swRegistration !== null) {
            swRegistration.getNotifications({ tag: thetag }).then(function (notifications) {
                notifications.forEach(notification => notification.close());
            });
        }

    }

    /**
     * Inits the Push notifications
     * @todo Create a Button to ask for permission!?
     */
    const init_push_notifications = async () => {
        check();
        swRegistration = await registerServiceWorker();
        const permission = await requestNotificationPermission();
    }

    //Push Notifications
    const check = () => {
        if (!('serviceWorker' in navigator)) {
            throw new Error('No Service Worker support!')
        }
        if (!('PushManager' in window)) {
            throw new Error('No Push API Support!')
        }
    }

    //function to register a service worker.
    const registerServiceWorker = async () => {
        const swRegistration = await navigator.serviceWorker.register(easyTimeTrackerGlobalVars.wp_frontend_url + 'include/scripts/tt-push-service.min.js');
        return swRegistration;
    }
    /**
     * Asks for the permission. If granted, nothing happen. Else it throws an error.
     */
    const requestNotificationPermission = async () => {
        const permission = await window.Notification.requestPermission();
        if (permission !== 'granted') {
            throw new Error('Permission not granted for Notification');
        }
    }

    /**
     * Initializes the products dropdown.
     * Creates a new Choices instance for the products dropdown.
     * @see https://github.com/jshjohnson/Choices
     */
    function init_products_dropdown() {
        if (product_select_choices !== null) {
            product_select_choices.destroy();
        }
        product_select_choices = new Choices(document.getElementById('tt_customer_products_select'), {
            shouldSort: false
        });
    }

    return {
        /**
         * Initializes the script
         * Adds the event listeners for the customer and product dropdown
         * Initializes the Push notifications
         * Gets the active trackers and sets the interval
         * @see https://github.com/jshjohnson/Choices
         */
        init: function () {
            document.addEventListener('DOMContentLoaded', function () {

                customer_select_choices = new Choices(document.getElementById('tt_customer_select'), {
                    itemSelectText: __('Select client', 'ett-easy-time-tracker-for-woocommerce'),
                    shouldSort: false,
                    allowHTML: true,
                    callbackOnCreateTemplates(template) {
                        return {
                            choice: (classNames, data) => {
                                return template(format_dropdown_client(data, classNames))
                            }
                        }
                    }
                });

                //Load the products when customer changes
                document.querySelector('#tt_customer_select').addEventListener('choice', async function () {
                    tt_clear_log();
                    if (this.options[this.selectedIndex].value !== 'null') {
                        document.getElementById('tt_customer_products_select').html === "";
                        await get_products(this.value);
                    }
                    hide_time_tracker();
                });

                //Eventlistener for update description. This will save the description to the local storage on change. 
                document.getElementById('tt_meta_description').addEventListener('change', function () {
                    send_heartbeat();
                });

                document.querySelector('#tt_customer_products_select').addEventListener('change', function (event) {
                    const selectedOption = event.target.options[event.target.selectedIndex];
                    const qty = selectedOption.getAttribute('data-current_qty');
                    tt_clear_log();

                    if (selectedOption.value === "link") {
                        var customerSelectedOption = document.querySelector('#tt_customer_select').options[document.querySelector('#tt_customer_select').selectedIndex];
                        var url = easyTimeTrackerGlobalVars.wp_backend_url + customerSelectedOption.value; //http://wpsite/wp-admin/post.php?action=edit&post=ID
                        var win = window.open(url, '_blank');
                        hide_product_dropdown();
                        document.querySelector('#tt_customer_select > option:first-child').selected = true;
                        win.focus();
                    } else if (selectedOption.value !== "null") {
                        show_time_tracker(qty);
                    } else {
                        hide_time_tracker();
                    }
                });

                document.getElementById('tt_action_button').addEventListener('click', function (btn) {
                    btn.preventDefault();
                    time_tracker_click(btn.target);
                });
                document.getElementById('tt_action_fix_time_button').addEventListener('click', function (btn) {
                    btn.preventDefault();
                    time_tracker_fix_click();
                });

                const active_trackers = document.querySelector("#tt_active_trackers");
                if (active_trackers) {
                    active_trackers.addEventListener("click", function (event) {

                        event.preventDefault();
                        //Check if the button is selected. If not, select the closest one.
                        const button = (event.target.nodeName !== 'BUTTON') ? event.target.closest('button') : event.target;
                        if (!button) {
                            return false;
                        }
                        if (button.matches('button.resume')) {
                            resume_tracking(JSON.parse(button.dataset.tracker));
                        }

                        if (button.matches('button.cancel')) {
                            cancel_tracking(JSON.parse(button.dataset.tracker));
                        }
                    });
                }

                init_push_notifications();

                //Get the active trackers and set the interval
                get_trackers();
                document.tt_active_trackers = window.setInterval(function () {
                    get_trackers();
                }, 60500);
            });
        },

        get_trackers: get_trackers,
        timetracker_data: timetracker_data,
        send_heartbeat: send_heartbeat,
        clear_tracking: clear_tracking,
        get_products: get_products,
        get_timetracker_inputs: get_timetracker_inputs

    };
})();
wc_timetracker.init();